import React, { useEffect, useState } from "react";
import { X, Loader2 } from "lucide-react";
import toast from "react-hot-toast";

const BRAND = "#55C5D0";

const TYPE_OPTIONS = [
  { value: "GENERAL_ENQUIRY", label: "General Enquiry" },
  { value: "INSPECTION_REQUEST", label: "Inspection Request" },
  { value: "DOCUMENT_REQUEST", label: "Document Request" },
  { value: "PRICE_ENQUIRY", label: "Price Enquiry" },
  { value: "OTHER", label: "Other" },
];

const EMPTY_FORM = { name: "", email: "", phone: "", type: "GENERAL_ENQUIRY", message: "" };

/**
 * Log an enquiry the agent took by phone/in person straight against a
 * property (Monday 12780350252: "Add a button 'Add an enquiry' to property
 * tab") — no buyer-portal submission involved, no emails sent.
 */
const AddEnquiryModal = ({ isOpen, onClose, onSubmit }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) setForm(EMPTY_FORM);
  }, [isOpen]);

  if (!isOpen) return null;

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Email is optional: buyers who only call or text often never give one
    // (Paul, Monday 13321586706). We still need a phone or an email to reach them.
    if (!form.name.trim() || !form.message.trim()) {
      toast.error("Name and message are required.");
      return;
    }
    if (!form.email.trim() && !form.phone.trim()) {
      toast.error("Please enter a phone number or an email.");
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(form);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] flex flex-col shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">Add an enquiry</h2>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <p className="text-xs text-gray-500">
            For an enquiry you took by phone or in person — this logs it against the property the same way an online enquiry would, without sending any email.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="block text-xs font-semibold text-gray-500 mb-1">Name <span className="text-red-500">*</span></span>
              <input type="text" value={form.name} onChange={(e) => set("name", e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/30 focus:border-[#55C5D0]"
                placeholder="Full name" />
            </label>
            <label className="block">
              <span className="block text-xs font-semibold text-gray-500 mb-1">Email <span className="font-normal text-gray-400">(optional)</span></span>
              <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/30 focus:border-[#55C5D0]"
                placeholder="name@example.com" />
            </label>
            <label className="block">
              <span className="block text-xs font-semibold text-gray-500 mb-1">Phone</span>
              <input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/30 focus:border-[#55C5D0]"
                placeholder="0400 000 000" />
            </label>
            <label className="block">
              <span className="block text-xs font-semibold text-gray-500 mb-1">Type</span>
              <select value={form.type} onChange={(e) => set("type", e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/30 focus:border-[#55C5D0]">
                {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
          </div>

          <label className="block">
            <span className="block text-xs font-semibold text-gray-500 mb-1">Message <span className="text-red-500">*</span></span>
            <textarea value={form.message} onChange={(e) => set("message", e.target.value)} rows={4}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/30 focus:border-[#55C5D0] resize-none"
              placeholder="What they asked / said" />
          </label>
        </form>

        <div className="px-6 py-4 border-t flex items-center justify-end gap-3">
          <button type="button" onClick={onClose} disabled={submitting}
            className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50">
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} disabled={submitting}
            className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg text-white font-semibold disabled:opacity-60"
            style={{ backgroundColor: BRAND }}>
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {submitting ? "Adding..." : "Add enquiry"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddEnquiryModal;
